import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { toast } from "sonner";

export default function ProductReviews({ slug }) {
  const { user } = useApp();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/products/${slug}/reviews`);
    setReviews(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/products/${slug}/reviews`, { rating, title, comment });
      toast.success("Avaliação publicada");
      setTitle(""); setComment(""); setRating(5);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    } finally { setSubmitting(false); }
  };

  return (
    <section className="mt-16" data-testid="reviews-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-display font-bold">Avaliações ({reviews.length})</h2>
      </div>

      {user ? (
        <form onSubmit={submit} className="rounded-2xl border border-border p-6 bg-card mb-6 space-y-4" data-testid="review-form">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">A tua avaliação</div>
            <div className="flex gap-1">
              {[1,2,3,4,5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)} data-testid={`review-star-${n}`}
                  className="p-1">
                  <Star className={`w-6 h-6 ${n <= rating ? "fill-yellow-400 text-yellow-400" : "text-muted"}`} />
                </button>
              ))}
            </div>
          </div>
          <Input placeholder="Título (opcional)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="review-title" />
          <Textarea rows={3} placeholder="Partilha a tua experiência..." value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" />
          <Button type="submit" className="rounded-full btn-primary" disabled={submitting} data-testid="review-submit">
            {submitting ? "A enviar..." : "Publicar avaliação"}
          </Button>
          <p className="text-xs text-muted-foreground">Só clientes que compraram o produto podem publicar. Todas as avaliações são verificadas.</p>
        </form>
      ) : (
        <div className="rounded-2xl border border-border p-6 bg-card mb-6 text-sm text-muted-foreground">
          Inicia sessão para deixar a tua avaliação (necessário ter comprado o produto).
        </div>
      )}

      <div className="space-y-3">
        {reviews.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-8">Sê o primeiro a avaliar este produto.</div>
        )}
        {reviews.map((r) => (
          <div key={r.id} className="rounded-2xl border border-border p-5 bg-card" data-testid={`review-${r.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">{r.user_name}</span>
                {r.verified && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-600 font-medium">✓ Comprador verificado</span>}
              </div>
              <div className="flex items-center gap-0.5">
                {[1,2,3,4,5].map((n) => <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-yellow-400 text-yellow-400" : "text-muted"}`} />)}
              </div>
            </div>
            {r.title && <div className="font-semibold text-sm mb-1">{r.title}</div>}
            {r.comment && <p className="text-sm text-muted-foreground leading-relaxed">{r.comment}</p>}
            <div className="text-[10px] text-muted-foreground mt-2">{new Date(r.created_at).toLocaleDateString("pt-PT")}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
